import React from 'react';

import styled from 'styled-components';

import { VerticalDivider } from 'components/lib/VerticalDivider';
import { getOrdinalSuffix } from 'components/View/utils';
import { AICompetitiveIntelligenceType } from 'types/recommendations';

import AiCiSectionIcon from './AiCiSectionIcon';
import ScorePill from './ScorePill';

const Container = styled.div`
  display: flex;
  flex-flow: row;
  gap: 20px;
`;

const MarketPositionVsCompetitors = styled.div`
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const OrdinalText = styled.div`
  font-size: 60px;
  line-height: 1;
  height: 70px;
  display: flex;
  align-items: flex-end;
`;

const OrdinalSuffix = styled.span`
  font-size: 28px;
`;

const ScorePillWrapper = styled.div`
  margin-top: 5px;
`;

const Recommendation = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  margin-top: 5px;
`;

const StrengthsSection = styled.div`
  flex: 1;
`;

const StrenghtsTitle = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
  display: flex;
  flex-flow: row;
  gap: 10px;
  align-items: center;
`;

const LimitationsTitle = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
  display: flex;
  flex-flow: row;
  gap: 10px;
  align-items: center;
`;

const LimitationsSection = styled.div`
  flex: 1;
`;

const List = styled.ul`
  margin-top: 15px;
  margin-bottom: 0;
  padding-left: 0;
  list-style: none;

  li {
    color: var(--text-secondary);
    font-size: 12px;
    display: flex;
    align-items: baseline;

    & + li {
      margin-top: 10px;
    }

    &::before {
      content: '';
      width: 4px;
      height: 4px;
      min-width: 4px;
      border-radius: 50%;
      margin-right: 8px;
      flex-shrink: 0;
      position: relative;
      top: -1px;
    }
  }
`;

const StrengthsList = styled(List)`
  li::before {
    background-color: var(--positive-green);
  }
`;

const LimitationsList = styled(List)`
  li::before {
    background-color: var(--inactive-orange);
  }
`;

const Image = styled.div`
  background-image: linear-gradient(to bottom, #edfcf7, #d0f6e8);
  padding: 3px;
  border-radius: 4px;
  line-height: 0;

  &.orange {
    background-image: linear-gradient(to bottom, #fcfced, #f6f1d0);
  }
`;

const NoData = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  margin-top: 15px;
`;

type Props = {
  data?: AICompetitiveIntelligenceType;
  title?: string;
  subtitle?: string;
  iconSrc?: string;
  iconVariant?: 'default' | 'action' | 'speed-to-lead';
};

const MarketPositionSummary = ({
  data,
  title = 'Market Position',
  subtitle = 'vs. Competitors',
  iconSrc = '/icons/stacked-bar-chart-black.svg',
  iconVariant = 'action',
}: Props) => {
  const strengths = data?.strengths ?? [];
  const limitations = data?.limitations ?? [];
  const ranking = data?.ranking;
  const summary = data?.summary;
  const overallScore = data?.overall_score;
  const hasOverallScore = typeof overallScore === 'number';
  const marketAverage = data?.competitor_average ?? null;

  return (
    <Container>
      <MarketPositionVsCompetitors>
        <Header>
          <AiCiSectionIcon src={iconSrc} variant={iconVariant} />
          <div>
            <Title>{title}</Title>
            <Subtitle>{subtitle}</Subtitle>
          </div>
        </Header>
        <OrdinalText>
          {ranking ? (
            <span>
              {ranking}
              <OrdinalSuffix>{getOrdinalSuffix(ranking).slice(String(ranking).length)}</OrdinalSuffix>
            </span>
          ) : (
            'No data'
          )}
        </OrdinalText>
        {hasOverallScore && (
          <ScorePillWrapper>
            <ScorePill score={overallScore} average={marketAverage} />
          </ScorePillWrapper>
        )}
        <Recommendation>{summary}</Recommendation>
      </MarketPositionVsCompetitors>
      <VerticalDivider />
      <StrengthsSection>
        <StrenghtsTitle>
          <Image>
            <img src="/icons/sentiment-satisfied-green.svg" width="16" height="16" />
          </Image>
          Your competitive advantage
        </StrenghtsTitle>
        {strengths.length > 0 ? (
          <StrengthsList>
            {strengths.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </StrengthsList>
        ) : (
          <NoData>No data</NoData>
        )}
      </StrengthsSection>
      <VerticalDivider />
      <LimitationsSection>
        <LimitationsTitle>
          <Image className="orange">
            <img src="/icons/sentiment-dissatisfied.svg" width="16" height="16" />
          </Image>
          Where you need to improve
        </LimitationsTitle>
        {limitations.length > 0 ? (
          <LimitationsList>
            {limitations.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </LimitationsList>
        ) : (
          <NoData>No data</NoData>
        )}
      </LimitationsSection>
    </Container>
  );
};

export default MarketPositionSummary;
